import React, { useState, useEffect } from 'react';
import { isElectron, isMac, windowControls } from '../utils/platform';
import { WindowControls } from './WindowControls';

interface LoadingScreenProps {
  message?: string;
  variant?: 'full' | 'page';
  showLogo?: boolean;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({
  message = 'Carregando...',
  variant = 'full',
  showLogo = true,
}) => {
  const [showSpinner, setShowSpinner] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    // Show spinner only if loading takes longer than animation (1800ms: 600ms logo + 720ms letters + 480ms buffer)
    const spinnerTimer = setTimeout(() => {
      setShowSpinner(true);
    }, 1800);

    return () => {
      clearTimeout(spinnerTimer);
    };
  }, []);

  useEffect(() => {
    if (!isElectron()) return;
    windowControls.isMaximized().then(setIsMaximized);
    const cleanup = windowControls.onStateChanged(async () => {
      setIsMaximized(await windowControls.isMaximized());
    });
    return cleanup;
  }, []);

  // Split ACCESS into individual letters
  const letters = ['A', 'C', 'C', 'E', 'S', 'S'];

  // Container classes based on variant
  const containerClasses =
    variant === 'full'
      ? 'fixed inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black'
      : 'flex items-center justify-center h-screen bg-white';

  return (
    <div className={containerClasses}>
      {/* Electron top bar — drag region + window controls */}
      {isElectron() && (
        <div className="fixed top-0 left-0 right-0 h-8 z-50 flex items-center justify-end electron-drag">
          {!isMac() && (
            <div className="electron-no-drag">
              <WindowControls isMaximized={isMaximized} />
            </div>
          )}
        </div>
      )}
      <div className="flex flex-col items-center">
        {/* Panther Logo with fade-in animation */}
        {showLogo && (
          <div className="mb-3 logo-fade-in">
            {/* panther-logo-mark-centered-white.svg, not panther-logo.svg: its square
                viewBox is centred on the SHIELD (the ® overhangs to the right, as in the
                artwork), so object-contain lands the shield dead centre instead of
                centring the shield+® box and letting the shield drift left. */}
            <img
              src="/panther-logo-mark-centered-white.svg"
              alt="Panther Logo"
              className="w-24 h-24 object-contain"
            />
          </div>
        )}

        {/* ACCESS Text with letter-by-letter animation */}
        <h2
          className="text-3xl font-bold text-white mb-8"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
          aria-label="ACCESS"
        >
          {letters.map((letter, index) => (
            <span
              key={index}
              className="inline-block opacity-0 letter-appear"
              style={{
                animationDelay: `${600 + index * 120}ms`,
                animationFillMode: 'forwards',
              }}
              aria-hidden="true"
            >
              {letter}
            </span>
          ))}
        </h2>

        {/* Loading Spinner - only shows after text animation completes */}
        {showSpinner && (
          <div className="mb-4 spinner-fade-in">
            <div className="w-16 h-16 border-4 border-[#96bf13] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Message text */}
        {showSpinner && (
          <p
            className="text-gray-400 text-sm message-fade-in"
            style={{ animationDelay: '200ms' }}
          >
            {message}
          </p>
        )}

      </div>
    </div>
  );
};

export default LoadingScreen;
